import { z } from "zod";

/**
 * Create Teams Bot Request Schema
 *
 * Creates an Azure Bot via MS Graph/Azure Management APIs.
 * Requires an existing OAuthConnection (TEAMS) with admin scopes.
 */
export const CreateTeamsBotBodySchema = z.object({
  /** Bot display name */
  botName: z.string().trim().min(1).max(100),

  /** OAuth connection ID for MS Graph access */
  oauthConnectionId: z.string().uuid(),

  /** Optional: deployment ID to set webhook endpoint */
  deploymentId: z.string().uuid().optional()
});

export type CreateTeamsBotBody = z.infer<typeof CreateTeamsBotBodySchema>;

/**
 * Create Teams Bot Response Schema
 *
 * Returns the created ApiKeyConnection info.
 */
export const CreateTeamsBotResponseSchema = z.object({
  /** Created ApiKeyConnection ID */
  connectionId: z.string().uuid(),

  /** Azure AD App ID (bot ID) */
  clientId: z.string(),

  /** User's Azure AD tenant ID */
  tenantId: z.string(),

  /** Azure resource group name */
  resourceGroupName: z.string(),

  /** Bot display name */
  botDisplayName: z.string()
});

export type CreateTeamsBotResponse = z.infer<typeof CreateTeamsBotResponseSchema>;

/**
 * Publish Teams Package Body Schema
 *
 * Optional manifest field overrides from confirmation modal.
 */
export const PublishTeamsPackageBodySchema = z
  .object({
    icon: z.string().optional(),
    shortName: z.string().optional(),
    fullName: z.string().optional(),
    shortDescription: z.string().min(1).max(80).optional(),
    fullDescription: z.string().min(80).max(4000).optional()
  })
  .optional();

export type PublishTeamsPackageBody = z.infer<typeof PublishTeamsPackageBodySchema>;

/**
 * Publish Teams Package Response Schema
 */
export const PublishTeamsPackageResponseSchema = z.object({
  message: z.string(),
  appId: z.string().optional() // MS Graph app catalog ID (if published successfully)
});

export type PublishTeamsPackageResponse = z.infer<typeof PublishTeamsPackageResponseSchema>;

/**
 * Publish Teams Package Path Variables Schema
 */
export const PublishTeamsPackagePathVarsSchema = z.object({
  id: z.string().uuid()
});

export type PublishTeamsPackagePathVars = z.infer<typeof PublishTeamsPackagePathVarsSchema>;
